import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import * as con from "../../GlobalConstants";
import * as sf from "./../logic/supportFunctions"
import { generateBands, modifyTerm } from "../../utils/bandFunctions";
import { AdjustMonthlyValuesToTerm, buildScenarioGenerator } from "../../utils/scenarioFunctions";
import { selectTermInMonths } from "../selectors/modelParametersSelector";
import { extractErrorMessage } from "../../utils/errorFunctions";
import { createError } from "../../templates/notifications/Notifications";

// ================= THUNKS =================

// FETCH
export const fetchModelParameters = createAsyncThunk(
  "modelParameters/fetch",
  async (_, { rejectWithValue }) => {
    try {
      let url = con.rest_base_url + con.transactions_api + con.model_parameters_api;

      // lógica surrogate
      if (sf.surrogateActive()) {
        url =
          con.rest_base_url +
          con.transactions_api +
          con.STAFF_API +
          con.model_parameters_api;
      }

      url = url + `${sf.getCurrentLine()}/`;

      const res = await axios.get(url, sf.getRequestConfig());

      return res.data;
    } catch (err) {
      return rejectWithValue(extractErrorMessage(err));
    }
  }
);

// EXPORT 
export const exportModelParameters = createAsyncThunk(
  "modelParameters/export",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const modelParameters = state[con.REDUCER_MODEL_PARAMETERS];
      
      // Clone state to avoid mutation
      const data = { ...modelParameters };
      
      // Enrich payload
      data[con.USER] = sf.getUser()[con.ID];
      data[con.USER_LINE] = sf.getCurrentLine();
      
      let staff = "";
      
      if (sf.surrogateActive()) {
        data[con.SURROGATE_USER] = sf.getSurrogateUser()[con.ID];
        data[con.USER] = sf.getSurrogateUser()[con.ID];
        staff = "/staff";
      }
      
      let url =
      con.rest_base_url +
      con.transactions_api +
      staff +
      con.model_parameters_api;
      
      url = url + `${sf.getCurrentLine()}/`;

      await axios.put(url, data, sf.getRequestConfig());

      return true;

    } catch (err) {
      return rejectWithValue(extractErrorMessage(err));
    }
  }
);


export const updateTermInMonths = (key, newValue) => 
  (dispatch, getState) => {

    dispatch(setModelParameter({
      id: key,
      value: newValue
    }));

    const state = getState();
    
    const term_in_months = selectTermInMonths(state);
    dispatch(setModelParameter({
      id: con.TERM_IN_MONTHS,
      value: term_in_months
    }));

    const modelParams = state[con.REDUCER_MODEL_PARAMETERS];
    const spot = state[con.REDUCER_APPLICATION_PARAMETERS][con.SPOT];

    const scenario =
      state[con.REDUCER_SCENARIOS].byId[con.SCENARIO_VINCO];

    const volatility =
      state[con.REDUCER_MARKET_VALUES][con.MARKET_VOLATILITY];

    const percentile =
      modelParams[con.ESC_CONFIDENCE_LEVEL];

    const generator = buildScenarioGenerator(
      scenario,
      { volatility, percentile }
    );

    let customValues = modelParams[con.CUSTOM_SCENARIO_VALUES];

    if (!customValues) {
      customValues = Array(term_in_months + 1)
        .fill(0)
        .map((_, i) =>
          generator(spot, con.DAYS_IN_MONTH * i)
        );
    }

    customValues = AdjustMonthlyValuesToTerm(
      customValues,
      term_in_months
    );

    dispatch(setModelParameter({
      id: con.CUSTOM_SCENARIO_VALUES,
      value: customValues
    }));
  };

// ================= INITIAL STATE =================

const initialState = {
  // DATA
  [con.CLIENT_TYPE]: con.IMPORTER,
  [con.PERIODICITY]: "MONTH",
  [con.AVERAGE_FOR_PERCENTAGE]: 0.8,
  [con.INITIAL_COVERAGE]: 0.7,
  [con.BAND_WIDTH]: 0.1,
  [con.GRADIENT]: 0.05,
  [con.ESC_PERCENTUAL_CHANGE]: 0.2,
  [con.ESC_CONFIDENCE_LEVEL]: 0.95,
  [con.MID_BAND_VALUES]: [0.8,0.7,0.66,0.63,0.6,0.57,0.54,0.51,0.49,0.46,0.44],
  [con.CUSTOM_SCENARIO_VALUES]: null,
  [con.AUTOMATIC_BANDS]: true,
  [con.CUSTOM_SCENARIO_ACTIVE]: false,
  [con.CUSTOM_FORWARD_CURVE]: null,
  [con.CUSTOM_FORWARD_CURVE_ACTIVE]: false,
  [con.TERM_PERIODICITY]: 10,
  [con.PASSTHROUGH]: 0,

  // GRID STRATEGY PARAMETERS
  [con.AUTOMATIC_GRID]: true,
  [con.CAN_FWDS]: false,
  [con.CAN_OPTIONS]: false,
  [con.CAN_SPOT]: false,
  [con.CONSIDERED_SPOT_COVERAGE]: false,
  [con.FWDS_MULTIPLE]: 250000,
  [con.OPTIONS_MULTIPLE]: 250000,
  [con.SPOT_MULTIPLE]: 250000,
  [con.STRATEGY_SPEED]: con.MODERATE,
  [con.GRID_TERM]: con.MEDIUM,
  [con.STRATEGY_TYPE]: con.RISK_MANAGEMENT,
  [con.WEEKS_TO_WAIT]: 2,
  [con.START_COVERAGE]: false,
  [con.INITIAL_DAYS_GRID]: false,

  // STATUS
  [con.MODEL_PARAMETERS_FETCHED]: con.NOT_STARTED,
  [con.MODEL_PARAMETERS_SAVED]: con.NOT_STARTED,
};

// ================= SLICE =================

const modelParametersSlice = createSlice({
  name: con.REDUCER_MODEL_PARAMETERS,
  initialState,
  reducers: {
    setModelParameter: (state, action) => {
      const { id, value } = action.payload;
      state[id] = value;

      if (id !== con.MODEL_PARAMETERS_SAVED) {
        state[con.MODEL_PARAMETERS_SAVED] = con.PENDING;
      }

      if (id === con.TERM_PERIODICITY) {
        state.mid_band_values = modifyTerm(
          state.mid_band_values,
          state.term_periodicity,
          state.initial_coverage,
          state.gradient
        )}
      else if (id === con.INITIAL_COVERAGE ||
               id === con.GRADIENT) {
          state.mid_band_values = generateBands(
            state.term_periodicity,
            state.initial_coverage,
            state.gradient
          )};
    },

    // REPLACE ALL
    replaceModelParameters: (state, action) => {
      Object.assign(state, action.payload);
    },

    setSpecificBand: (state, action) => {
      const { index, value } = action.payload;
      state.mid_band_values[index] = value;
    },
  },

  extraReducers: (builder) => {
    builder

      // ===== FETCH =====
      .addCase(fetchModelParameters.pending, (state) => {
        state[con.MODEL_PARAMETERS_FETCHED] = con.LOADING;
      })
      .addCase(fetchModelParameters.fulfilled, (state, action) => {
        if (Object.keys(action.payload).length > 0) {
          Object.assign(state, action.payload);
        }
        state[con.MODEL_PARAMETERS_FETCHED] = con.OK;
      })
      .addCase(fetchModelParameters.rejected, (state, action) => {
        state[con.MODEL_PARAMETERS_FETCHED] = con.ERROR;
        createError(action.payload);
      })

      // ===== EXPORT =====
      .addCase(exportModelParameters.pending, (state) => {
        state[con.MODEL_PARAMETERS_SAVED] = con.LOADING;
      })
      .addCase(exportModelParameters.fulfilled, (state) => {
        state[con.MODEL_PARAMETERS_SAVED] = con.OK;
      })
      .addCase(exportModelParameters.rejected, (state, action) => {
        state[con.MODEL_PARAMETERS_SAVED] = con.ERROR;
        createError(action.payload);
      })
  },
});

// ================= EXPORTS =================

export const {
  setModelParameter,
  replaceModelParameters,
  setSpecificBand,
} = modelParametersSlice.actions;

export default modelParametersSlice.reducer;